/**
 * The topic-hub template's pure decisions (THE-587, design in THE-577 plan §4
 * Slice B). Routing helpers, the facet vocabularies, and — the part with a
 * right answer — how a `theaWebSearchGifts` response becomes a screen.
 *
 * Pure and Firebase-free for the same reason `hubSections` is: it lets the view
 * render from Storybook, and it lets the rules below be tested against the
 * retrieval eval's own measured numbers rather than against a mock of our own
 * invention.
 *
 * WHY THIS FILE EXISTS AT ALL — the empty state cannot be written by eye.
 * THE-585 measured the two collections over 53 synthesised and 5 real logged
 * queries, and the headline is that `results.length === 0` is DEAD CODE on this
 * surface: `products` carries a vector arm with `k`, so it always returns a
 * full page, and a real tester's nonsense query `quokka tuxedo zither` came
 * back with 21 products and 10 guides (top hit a Quokka-brand board game, the
 * rest kalimbas and tongue drums). Gate the screen on count and the visitor
 * gets a confident wall of wrong items instead of an empty state. So every
 * decision here is made on the server's relevance verdict, never on a length.
 *
 * Full derivation, with the per-query numbers behind each rule, is the
 * `build-constraints` document on THE-587.
 */
import type {
  GiftSearchAgeGroup,
  TheaWebSearchGiftsResponse,
} from '../schemas/endpoints/theaWebSearchGifts';

/**
 * The arbitrary-search route. A RESERVED slug under the same prefix, not a
 * sibling route, because the plan's §4 requirement is that a typed query and a
 * curated topic render the identical template — making `/gift-guides/search`
 * one of the topic paths is what makes "the same template" structural instead
 * of a thing two components have to agree about.
 *
 * It follows that no occasion key may ever be `search`. That is enforced in
 * `isReservedTopicSlug` and asserted in the tests, rather than left to whoever
 * next edits `theaWebHubOccasions/current`.
 */
export const HUB_SEARCH_SLUG = 'search';

/** `?q=` on the search route. */
export const HUB_SEARCH_QUERY_PARAM = 'q';

/** Curated topic path — `/gift-guides/dad-birthday`. */
export function topicHubPath(topicKey: string): string {
  return `/gift-guides/${encodeURIComponent(topicKey)}`;
}

/**
 * Arbitrary-search path — `/gift-guides/search?q=cheap+stocking+stuffers`.
 * `URLSearchParams` rather than `encodeURIComponent` so a space becomes `+` and
 * the round trip through `useSearchParams` is lossless.
 */
export function hubSearchPath(query: string): string {
  const params = new URLSearchParams({ [HUB_SEARCH_QUERY_PARAM]: query });
  return `/gift-guides/${HUB_SEARCH_SLUG}?${params.toString()}`;
}

export function isReservedTopicSlug(slug: string): boolean {
  return slug === HUB_SEARCH_SLUG;
}

/**
 * Age filter. These are the 10 `DetailedAgeGroup` bands, and the labels are the
 * QUIZ's own words — the visitor met these exact bands there, so inventing a
 * second vocabulary for the hub would make the same filter read differently on
 * two surfaces. Order is youngest-first, the order the quiz presents them in.
 */
export const HUB_AGE_OPTIONS: ReadonlyArray<{ value: GiftSearchAgeGroup; label: string }> = [
  { value: 'NEWBORN_0_2_MONTHS', label: 'Newborn (0-2 months)' },
  { value: 'YOUNG_INFANT_2_6_MONTHS', label: 'Baby (2-6 months)' },
  { value: 'OLDER_INFANT_6_12_MONTHS', label: 'Baby (6-12 months)' },
  { value: 'TODDLER_1_3_YEARS', label: 'Toddler (1-3)' },
  { value: 'PRESCHOOL_3_5_YEARS', label: 'Preschool (3-5)' },
  { value: 'ELEMENTARY_6_10_YEARS', label: 'Kids (6-10)' },
  { value: 'MIDDLE_SCHOOL_11_14_YEARS', label: 'Tweens (11-14)' },
  { value: 'HIGH_SCHOOL_14_18_YEARS', label: 'Teens (14-18)' },
  { value: 'YOUNG_ADULT', label: 'Young adults' },
  { value: 'OLDER_ADULT', label: 'Adults' },
];

/**
 * How many items the carousel shows before "See all" swaps it for the grid.
 * Matches the trending row's visual density; the rest of the page is the same
 * whichever mode is showing, so this is presentation, not a fetch size.
 */
export const HUB_CAROUSEL_PREVIEW_COUNT = 8;

/**
 * What the items half of the page should render.
 *
 *  - `results`        the grid. The verdict was `high`, or `unknown` (which
 *                     fails OPEN — see below).
 *  - `low_confidence` the catalogue has no vocabulary for this. `dark academia`
 *                     0.247, `arrives tomorrow` 0.259.
 *  - `curated_trending` the visitor asked for a RANKING, not a semantic match.
 *                     The hub renders its own curated row instead; this is NOT
 *                     an empty state and must not read like one.
 */
export type HubItemsMode = 'results' | 'low_confidence' | 'curated_trending';

/**
 * What the guides rail should say. Three states, not two, because
 * `guidesRelevance` can tell "we found candidates and none were close enough"
 * from "the corpus had nothing / the search was unavailable" — and those are
 * different sentences.
 */
export type HubGuidesMode = 'rail' | 'none_close_enough' | 'nothing_tried';

export interface HubPresentation {
  items: HubItemsMode;
  guides: HubGuidesMode;
  /**
   * Excluded phrases the server parsed out of the query, e.g. `['pink']`.
   * Empty when the query carried no exclusion. Present here as its own field
   * because NOTHING else in the response reveals it: an inverted negation
   * scores `confidence: 'high'` by construction, so the grid renders happily
   * while showing the visitor the one thing they said they did not want.
   */
  negationTerms: string[];
  /**
   * How many returned items literally mention an excluded term. A lexical LOWER
   * BOUND, not a count of offenders — it reads 0 on the worst real case
   * (`doesn't like clutter`), which is why it may only strengthen the message
   * and must never gate it.
   */
  negationLiteralMatches: number;
  /** The price cap actually applied, and where it came from. Rendered always.
   *
   * `'cleared'` (THE-833) carries `priceMax: null` and means the visitor
   * dismissed a cap the server had parsed — as against a plain `null` source,
   * which means there was never a cap to dismiss. Same `priceMax`, different
   * chip state to restore on a back-navigation. */
  priceMax: number | null;
  priceMaxSource: 'query' | 'request' | 'cleared' | null;
}

/**
 * The response -> screen decision. ORDER IS LOAD-BEARING and is the single
 * easiest thing to get wrong here.
 *
 * `intent` IS CHECKED BEFORE `confidence`, because the two are not a hierarchy
 * of the same judgement — they are different questions, and the answers
 * collide. Measured: `trending` scores 0.265 and `dark academia` scores 0.247.
 * Both are `confidence: 'low'`. One wants the curated trending row and one
 * wants a genuine "we don't have the words for this" state. Reading
 * `confidence` first collapses them into one screen and gets one of them
 * wrong every time.
 *
 * `unknown` FAILS OPEN, to results rather than to an empty state. "We could not
 * judge" is not evidence of a bad answer, and the products rail is the one that
 * can never legitimately be empty.
 *
 * The guides decision is INDEPENDENT of the items decision, deliberately. On
 * multi-constraint queries the two collections drop opposite halves of the
 * intent — `gift for a new dad who loves grilling` returns genuinely good
 * grilling gear with no new-dad signal, while guides returns 5 baby guides with
 * no grilling signal. A guides rail that only appeared when items failed would
 * never render there, because the items came back GOOD, and yet guides is
 * carrying the half the items lost. So: no shared `isEmpty`, and no nesting.
 */
export function resolveHubPresentation(
  response: TheaWebSearchGiftsResponse,
): HubPresentation {
  const { query, relevance, guides, guidesRelevance } = response;

  let items: HubItemsMode;
  if (query.intent === 'navigational') {
    items = 'curated_trending';
  } else if (relevance.confidence === 'low') {
    items = 'low_confidence';
  } else {
    // 'high', and 'unknown' failing open.
    items = 'results';
  }

  let guidesMode: HubGuidesMode;
  if (guides.length > 0) {
    // ALREADY GATED SERVER-SIDE by both of production's guide gates (floor and
    // margin, imported from `guide_matcher` so they cannot drift into a second
    // language). Re-filtering here would be a second copy of two thresholds.
    guidesMode = 'rail';
  } else if (guidesRelevance.considered === 0) {
    guidesMode = 'nothing_tried';
  } else {
    guidesMode = 'none_close_enough';
  }

  return {
    items,
    guides: guidesMode,
    // The presence of `negation` is the signal. `literalMatches` is NOT: it is
    // a lexical lower bound and reads 0 on `doesn't like clutter`, where the
    // catalogue expresses the excluded concept without using the word.
    negationTerms: query.negation ? query.negation.terms : [],
    negationLiteralMatches: query.negation ? query.negation.literalMatches : 0,
    priceMax: query.priceMax,
    priceMaxSource: query.priceMaxSource,
  };
}

/**
 * The sentence for an excluded term, e.g. `['pink']` -> "not pink". Joined with
 * "or" rather than "and" because the visitor excluded each one independently.
 */
export function negationSummary(terms: string[]): string {
  if (!terms.length) return '';
  if (terms.length === 1) return `not ${terms[0]}`;
  return `not ${terms.slice(0, -1).join(', ')} or ${terms[terms.length - 1]}`;
}
